import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { loadClientPortalPayments, uploadClientPortalPaymentProof, type ClientPortalPaymentOptions } from './service'

type Target = { id: string; kind: 'stage' | 'installment'; label: string; balance: number; status: string }
const money = (amount: number, currency: string) => `${currency === 'USD' ? 'US$' : 'RD$'} ${Number(amount).toLocaleString('es-DO',{minimumFractionDigits:2})}`

function credentials() {
  const code = (document.getElementById('caseCodeInput') as HTMLInputElement | null)?.value || ''
  const token = (document.getElementById('caseTokenInput') as HTMLInputElement | null)?.value || ''
  return { caseCode: code.trim().toUpperCase(), accessToken: token.trim().toUpperCase() }
}

export function ClientPortalPayments() {
  const [mount, setMount] = useState<HTMLElement | null>(null)
  const [data, setData] = useState<ClientPortalPaymentOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState('')
  const [targetKey, setTargetKey] = useState('')
  const [amount, setAmount] = useState('')
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0,10))
  const [reference, setReference] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const load = async () => {
    const access = credentials()
    if (!access.caseCode || !access.accessToken) return
    setLoading(true); setMessage('')
    try { setData(await loadClientPortalPayments(access.caseCode, access.accessToken)) }
    catch (error) { setMessage(error instanceof Error ? error.message : 'No se pudo cargar el plan.') }
    finally { setLoading(false) }
  }

  useEffect(() => {
    const ensureMount = () => {
      const body = document.querySelector('#caseStatusContent .form-card__body')
      if (!(body instanceof HTMLElement)) return
      let node = document.getElementById('clientPaymentPortalMount')
      if (!node) { node = document.createElement('div'); node.id = 'clientPaymentPortalMount'; body.appendChild(node) }
      setMount(node)
    }
    const onStatus = (event: Event) => {
      ensureMount()
      const status = (event as CustomEvent).detail?.status
      if ((event as CustomEvent).detail?.ok && (status?.status === 'converted' || status?.plan_status === 'active')) void load()
    }
    ensureMount()
    window.addEventListener('siga:case-status-result', onStatus)
    return () => window.removeEventListener('siga:case-status-result', onStatus)
  }, [])

  const targets = useMemo<Target[]>(() => {
    if (!data?.plan) return []
    const installmentStageIds = new Set(data.installments.map(item => item.stage_id).filter(Boolean))
    const stages = data.stages
      .filter(item => Number(item.balance) > 0 && !['paid','cancelled','locked'].includes(item.status) && !installmentStageIds.has(item.id))
      .map(item => ({ id:item.id, kind:'stage' as const, label:item.label, balance:Number(item.balance), status:item.status }))
    const installments = data.installments
      .filter(item => Number(item.balance) > 0 && !['paid','cancelled','locked'].includes(item.status))
      .map(item => ({ id:item.id, kind:'installment' as const, label:item.label || `Cuota ${item.installment_number}`, balance:Number(item.balance), status:item.status }))
    return [...stages,...installments]
  }, [data])
  const target = targets.find(item => `${item.kind}:${item.id}` === targetKey) ?? null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!data?.plan || !target || !file) return setMessage('Selecciona el concepto y el archivo.')
    setSending(true); setMessage('')
    try {
      const access = credentials()
      await uploadClientPortalPaymentProof({
        ...access, planId:data.plan.id,
        stageId:target.kind === 'stage' ? target.id : null,
        installmentId:target.kind === 'installment' ? target.id : null,
        file, amount:Number(amount), currency:data.plan.currency, paymentDate, reference,
      })
      setMessage('Comprobante enviado. Quedó pendiente de revisión por Cobranza.')
      setTargetKey(''); setAmount(''); setReference(''); setFile(null)
      await load()
    } catch (error) { setMessage(error instanceof Error ? error.message : 'No se pudo enviar el comprobante.') }
    finally { setSending(false) }
  }

  if (!mount || (!loading && !data?.plan && !message)) return null
  return createPortal(<section className="client-payment-portal-card">
    <div className="client-payment-portal-head"><div><p className="eyebrow">Portal de pagos</p><h3>Comprobantes del plan</h3></div>{data?.plan && <span className="lozenge lozenge--success">{data.plan.plan_number}</span>}</div>
    {loading ? <p className="help">Cargando plan de pago…</p> : data?.plan ? <>
      <p className="help">{data.client_name} · {data.client_code ?? 'cliente'} · {data.plan.unit_reference ?? 'sin unidad'}</p>
      {targets.length ? <form onSubmit={submit} className="client-payment-portal-form">
        <div className="field"><label>Etapa o cuota</label><select className="select" required value={targetKey} onChange={event => { const key=event.target.value; setTargetKey(key); const selected=targets.find(item=>`${item.kind}:${item.id}`===key); setAmount(selected ? String(selected.balance) : '') }}><option value="">Seleccionar…</option>{targets.map(item => <option key={`${item.kind}:${item.id}`} value={`${item.kind}:${item.id}`}>{item.label} · {money(item.balance,data.plan!.currency)}</option>)}</select></div>
        <div className="form-grid"><div className="field"><label>Monto ({data.plan.currency})</label><input className="input" type="number" min="0.01" step="0.01" max={target?.balance} required value={amount} onChange={event=>setAmount(event.target.value)} /></div><div className="field"><label>Fecha de pago</label><input className="input" type="date" required value={paymentDate} onChange={event=>setPaymentDate(event.target.value)} /></div></div>
        <div className="field"><label>Referencia bancaria</label><input className="input" required maxLength={120} value={reference} onChange={event=>setReference(event.target.value)} /></div>
        <div className="field"><label>Comprobante</label><input className="input" type="file" accept="image/jpeg,image/png,application/pdf" required onChange={event=>setFile(event.target.files?.[0] ?? null)} /><p className="help">JPG, PNG o PDF · máximo 10 MB.</p></div>
        <button className="btn btn--primary" type="submit" disabled={sending}>{sending ? 'Enviando…' : 'Enviar a Cobranza'}</button>
      </form> : <div className="notice"><div>No hay etapas o cuotas habilitadas pendientes.</div></div>}
    </> : null}
    {message && <div className="notice" role="status"><div>{message}</div></div>}
  </section>, mount)
}
